import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getContentSources,
  addContentSource,
  updateContentSource,
  deleteContentSource,
} from '../../api/etl.api';
import {
  CreateEtlContentSourceDto,
  UpdateEtlContentSourceDto,
} from '@inference.engine/api-client/web';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';

export const useEtlContentSources = () => {
  return useQuery({
    queryKey: ['etl-content-sources'],
    queryFn: getContentSources,
  });
};

export const useAddContentSource = () => {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contentSource: CreateEtlContentSourceDto) => addContentSource(contentSource),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['etl-content-sources'] });
      notificationService.showSuccess(
        t('admin.useEtlContentSources.contentSourceAddedSuccessfully'),
      );
    },
    onError: (error) => {
      notificationService.showError(t('admin.useEtlContentSources.failedToAddContentSource'));
      logger.error('Failed to add content source:', error);
    },
  });
};

export const useUpdateContentSource = () => {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contentSource: UpdateEtlContentSourceDto) => updateContentSource(contentSource),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['etl-content-sources'] });
      notificationService.showSuccess(
        t('admin.useEtlContentSources.contentSourceUpdatedSuccessfully'),
      );
    },
    onError: (error) => {
      notificationService.showError(t('admin.useEtlContentSources.failedToUpdateContentSource'));
      logger.error('Failed to update content source:', error);
    },
  });
};

export const useDeleteContentSource = () => {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteContentSource(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['etl-content-sources'] });
      notificationService.showSuccess(
        t('admin.useEtlContentSources.contentSourceDeletedSuccessfully'),
      );
    },
    onError: (error) => {
      notificationService.showError(t('admin.useEtlContentSources.failedToDeleteContentSource'));
      logger.error('Failed to delete content source:', error);
    },
  });
};
